import { FormAutocomplete, Switch, FormInput, FormDatePicker, Button, InputLabel, ConfirmationModal } from 'components'
import { colors, CONSTANTS } from 'constants/index'
import React, { useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { useSelector, useDispatch } from 'react-redux'
import { formatDecimal, getDateWithTZ } from '../../utils/getters/common'
import axios from 'axios'
import { getCaseStatusPath } from 'utils/path-helpers/api'
import { openSnackbar } from 'store/actions/common'
import { getProCaseFullInfo } from 'store/actions/cases'

const useStyles = makeStyles({
  standardMargin: {
    marginBottom: 16,
  },
  switchBase: {
    background: 'none',
    '&:hover': {
      background: 'none',
    },
  },
  colorPrimary: {
    '&.Mui-checked': {
      color: colors.WHITE,
      '& + .MuiSwitch-track': {
        opacity: 1,
        backgroundColor: colors.PRIMARY.COLOR[50],
      },
    },
    '&.Mui-disabled': {
      color: colors.WHITE,
      '& + .MuiSwitch-track': {
        backgroundColor: colors.PRIMARY.COLOR[50],
        opacity: 0.5,
      },
    },
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  selections: {
    flexGrow: 1,
  },
  billingForm: {
    marginTop: 16,
    marginBottom: 16,
    paddingBottom: 16,
    borderBottom: `1px solid ${colors.BASIC.COLOR[10]}`,
  },
  billingFormTitle: {
    marginBottom: 16,
    fontWeight: 500,
  },
  twoColumnRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 16,
  },
  twoColumn: {
    width: '48%',
  },
  stopBillingButton: {
    marginTop: 16,
  },
})

const invoicingOptions = Object.values(CONSTANTS.INVOICING)
const invoiceFrequencyOptions = Object.values(CONSTANTS.INVOICE_FREQUENCY)

// Billing frequency options
const billingFrequencyTypeOptions = [
  { value: 1, label: 'Week' },
  { value: 2, label: 'Month' },
]

const billingStopTypeOptions = [
  { value: 1, label: 'Continue until stopped manually' },
  { value: 2, label: 'By due date' },
]

const getInvoicingHint = invoicingType => {
  return CONSTANTS.INVOICING[invoicingType] ? CONSTANTS.INVOICING[invoicingType].hint : ''
}

const ExpenseInvoicingSelection = props => {
  const classes = useStyles()
  const { caseData, onChange, mode, errors, setErrors = error => {} } = props
  const currentUser = useSelector(state => state.auth.currentUser)
  const dispatch = useDispatch()
  const isAccountant = currentUser?.subrole === 'accountant'
  const contactInfo = useSelector(state => state.profileCreation.profileData.contactInfo)
  const userCountry = contactInfo?.country || currentUser?.country || 840 // Default to USA
  const isSharedWithMe = caseData?.is_shared && !caseData?.is_owner

  const [showStopBillingModal, setShowStopBillingModal] = useState(false)
  const getCurrencySymbol = () => {
    return userCountry === 124 ? 'C$' : '$' // 124 is Canada, 840 is USA
  }

  const handleStopBilling = async () => {
    try {
      await axios.patch(getCaseStatusPath(caseData.uuid), {
        status: 'stopped',
      })
      dispatch(openSnackbar('success', `${isAccountant ? 'Project' : 'Case'} billing stopped`))
      // Refetch case details after status change
      dispatch(getProCaseFullInfo(caseData.uuid))
    } catch (e) {
      dispatch(openSnackbar('error', e?.response?.data?.detail || 'Error stopping billing'))
    } finally {
      setShowStopBillingModal(false)
    }
  }

  const handleRestartBilling = async () => {
    try {
      await axios.patch(getCaseStatusPath(caseData.uuid), {
        status: 'pending',
      })
      dispatch(openSnackbar('success', `${isAccountant ? 'Project' : 'Case'} billing restarted`))
      // Refetch case details after status change
      dispatch(getProCaseFullInfo(caseData.uuid))
    } catch (e) {
      dispatch(openSnackbar('error', 'Error restarting billing'))
    }
  }

  const handleSwitchChange = (e, v) => {
    onChange({ expense_invoicing: v, invoice_frequency: null, invoicing: null })
    setErrors({
      expense_invoicing: '',
      invoice_frequency: '',
      invoicing: '',
    })
  }

  const handleChange = (name, value) => {
    if (name === 'first_billing_date') {
      onChange({
        [name]: value,
        ['last_billing_date']: value,
      })
    } else {
      onChange({
        [name]: value,
      })
    }
    setErrors({
      [name]: '',
    })
  }

  return (
    <div>
      {isAccountant && caseData.fee_type === 4 && (
        <div className={classes.billingForm}>
          <div className={classes.billingFormTitle}>Invoicing schedule</div>

          <div className={classes.twoColumnRow}>
            <div className={classes.twoColumn}>
              <FormInput
                label={`Amount, ${getCurrencySymbol()}`}
                disabled={
                  !(
                    mode === CONSTANTS.MODE.ADD ||
                    ((caseData.status === 'stopped' || caseData.status === 'rejected') && mode !== CONSTANTS.MODE.VIEW)
                  )
                }
                value={caseData.amount}
                inputProps={{ style: { textAlign: 'right' } }}
                fullWidth
                containerClass={classes.standardMargin}
                name="amount"
                error={errors.amount}
                onChange={handleChange}
                handleBlur={e => handleChange('amount', formatDecimal(e.target.value))}
                type="number"
              />
            </div>
            <div className={classes.twoColumn}>
              <InputLabel classes={{ marginBottom: 0 }}>Repeat billing every</InputLabel>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  position: 'relative',
                  top: '-7px',
                }}
              >
                <div style={{ flex: 1, marginRight: 5 }}>
                  <FormInput
                    disabled={
                      !(
                        mode === CONSTANTS.MODE.ADD ||
                        ((caseData.status === 'stopped' || caseData.status === 'rejected') &&
                          mode !== CONSTANTS.MODE.VIEW)
                      )
                    }
                    value={caseData.billing_frequency_number || ''}
                    fullWidth
                    containerClass={classes.standardMargin}
                    name="billing_frequency_number"
                    error={errors.billing_frequency_number}
                    onChange={handleChange}
                    type="number"
                    inputProps={{
                      min: 1,
                      max: 12,
                    }}
                  />
                </div>
                <div style={{ flex: 2 }}>
                  <FormAutocomplete
                    disabled={
                      !(
                        mode === CONSTANTS.MODE.ADD ||
                        ((caseData.status === 'stopped' || caseData.status === 'rejected') &&
                          mode !== CONSTANTS.MODE.VIEW)
                      )
                    }
                    value={caseData.billing_frequency_type}
                    fullWidth
                    style={{ flex: 3 }}
                    containerClass={classes.standardMargin}
                    name="billing_frequency_type"
                    error={errors.billing_frequency_type}
                    onChange={handleChange}
                    options={billingFrequencyTypeOptions}
                    placeholder="Please select"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className={classes.twoColumnRow}>
            <div className={classes.twoColumn}>
              <FormDatePicker
                label="First billing date"
                disabled={
                  !(
                    mode === CONSTANTS.MODE.ADD ||
                    ((caseData.status === 'stopped' || caseData.status === 'rejected') && mode !== CONSTANTS.MODE.VIEW)
                  )
                }
                value={caseData.first_billing_date}
                onChange={handleChange}
                fullWidth
                disabledDays={{ before: getDateWithTZ(null) }}
                name="first_billing_date"
                error={errors.first_billing_date}
                containerClass={classes.standardMargin}
              />
            </div>
            <div className={classes.twoColumn}>
              <FormAutocomplete
                label="Stop billing"
                disabled={
                  !(
                    mode === CONSTANTS.MODE.ADD ||
                    ((caseData.status === 'stopped' || caseData.status === 'rejected') && mode !== CONSTANTS.MODE.VIEW)
                  )
                }
                value={caseData.billing_stop_type}
                fullWidth
                containerClass={classes.standardMargin}
                name="billing_stop_type"
                error={errors.billing_stop_type}
                onChange={handleChange}
                options={billingStopTypeOptions}
                placeholder="Please select"
              />
            </div>
          </div>
          {caseData.billing_stop_type === 2 && (
            <FormDatePicker
              label="Last billing date"
              disabled={
                !(
                  mode === CONSTANTS.MODE.ADD ||
                  ((caseData.status === 'stopped' || caseData.status === 'rejected') && mode !== CONSTANTS.MODE.VIEW)
                )
              }
              value={caseData.last_billing_date}
              onChange={handleChange}
              fullWidth
              disabledDays={{ before: getDateWithTZ(caseData.first_billing_date) }}
              name="last_billing_date"
              error={errors.last_billing_date}
              containerClass={classes.standardMargin}
            />
          )}

          {mode !== CONSTANTS.MODE.ADD &&
            (caseData.status === 'active' || caseData.status === 'pending') &&
            !isSharedWithMe && (
              <Button
                variant="secondary"
                className={classes.stopBillingButton}
                onClick={() => setShowStopBillingModal(true)}
              >
                Stop billing
              </Button>
            )}
          {mode !== CONSTANTS.MODE.ADD &&
            (caseData.status === 'stopped' || caseData.status === 'rejected') &&
            !isSharedWithMe && (
              <Button variant="secondary" className={classes.stopBillingButton} onClick={handleRestartBilling}>
                Restart billing
              </Button>
            )}
        </div>
      )}
      <div className={classes.row}>
        <div>
          <Switch
            label={isAccountant ? 'Other expenses/charges' : 'Expense invoicing'}
            checked={caseData.expense_invoicing}
            color="primary"
            onChange={handleSwitchChange}
            disabled={mode !== CONSTANTS.MODE.ADD}
            classes={{
              switchBase: classes.switchBase,
            }}
          />
        </div>
        <div className={classes.selections}>
          <FormAutocomplete
            label="Invoicing"
            disabled={mode !== CONSTANTS.MODE.ADD || !caseData.expense_invoicing}
            value={caseData.invoicing}
            fullWidth
            containerClass={classes.standardMargin}
            name="invoicing"
            error={errors.invoicing}
            onChange={handleChange}
            options={invoicingOptions}
            placeholder="Please select"
            hint={getInvoicingHint(caseData.invoicing)}
          />
          {caseData.invoicing === CONSTANTS.INVOICING[2].value && (
            <FormAutocomplete
              label="Invoice frequency"
              disabled={mode !== CONSTANTS.MODE.ADD || !caseData.expense_invoicing}
              value={caseData.invoice_frequency}
              fullWidth
              containerClass={classes.standardMargin}
              name="invoice_frequency"
              error={errors.invoice_frequency}
              onChange={handleChange}
              options={invoiceFrequencyOptions}
              placeholder="Please select"
            />
          )}
        </div>
      </div>
      <ConfirmationModal
        isOpen={showStopBillingModal}
        message={`If you stop billing for the ${isAccountant ? 'project' : 'case'} ${
          caseData.name
        }, no new invoices will be generated for future activities. Continue?`}
        onClose={() => setShowStopBillingModal(false)}
        title={`Stop billing for ${isAccountant ? 'project' : 'case'}?`}
        onConfirm={handleStopBilling}
        confirmationButtonText="yes, stop billing"
        cancelationButtonText="cancel"
      />
    </div>
  )
}

export default ExpenseInvoicingSelection
